import React, { useEffect, useState } from 'react'
import { Button, Card, Col, Container, Form, Modal, Row } from 'react-bootstrap'
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from 'react-redux'
import useAuth from '../../context/auth/useAuth'
import { getSchool } from '../../actions/schoolsActions'
import Swal from 'sweetalert2'
import { addSchoolStaff, deleteSchoolStaff, editSchoolStaff, getSchoolStaffsBySchool } from '../../actions/schoolStaffActions'
import { CustomDataTable } from '../../components/design/Datatables/CustomDataTable'

export default function SchoolStaffs() {
    const { t } = useTranslation()
	const {client} = useAuth()
    const dispatch = useDispatch()

    const {school} = useSelector(state=>state.schools)
    const {schoolStaffs, loading, result} = useSelector(state=>state.schoolStaffs)

    const [schoolStaffForm, setSchoolStaffForm] = useState(null)

    const schoolStaffsColumns = [
        {
            name: t('schoolStaff.name'),
            id: 'id',
            cell: schoolStaff =>
                schoolStaff.name,
            selector: (schoolStaff) => schoolStaff.name,
            sortable: true,
        },{
            name: t('schoolStaff.charge'),
            cell: schoolStaff =>
                schoolStaff.charge,
            selector: (schoolStaff) => schoolStaff.charge
        },{
            name: t('schoolStaff.email'),
            cell: schoolStaff =>
                schoolStaff.email,
            selector: (schoolStaff) => schoolStaff.email
        },{
            name: t('global.actions'),
            center: 'center',
            width: "120px",
            cell: schoolStaff =>
                <div>
                    <img src="/images/edit.svg" alt="Editar" className='cursor mx-2' onClick={() => {selectEditSchoolStaff(schoolStaff)}}/>
                    <img src="/images/trash.svg" className='cursor mx-2' alt="Eliminar" onClick={() => {handleDelete(schoolStaff)}}/>
                </div>
        }
    ]

    const handleSubmit = (e) => {
        e.preventDefault()
        if (!(schoolStaffForm?.name && schoolStaffForm?.charge && schoolStaffForm?.email)) {
            Swal.fire({
                showConfirmButton: true,
                icon: 'error',
                text: t('schoolStaff.formIncomplete')
            })
        } else {
            if (schoolStaffForm?.staffId) {
                dispatch(editSchoolStaff(schoolStaffForm.staffId, schoolStaffForm))
            } else {
                dispatch(addSchoolStaff({...schoolStaffForm, school: school._id}))
            }
        }
    }

    const selectEditSchoolStaff = (schoolStaff) => {
        setSchoolStaffForm({
            staffId: schoolStaff.staffId,
            name: schoolStaff.name,
            charge: schoolStaff.charge,
            email: schoolStaff.email,
            password: null
        })
    }

    const handleDelete = (schoolStaff) => {
        Swal.fire({
            title: t('schoolStaff.deleteTitle'),
            text: t('schoolStaff.deleteQuestion', {name: schoolStaff.name}),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            confirmButtonText: t('global.accept'),
            cancelButtonText: t('global.cancel')
        }).then((result) => {
            if (result.isConfirmed) {
                dispatch(deleteSchoolStaff(schoolStaff.staffId))
            }
        })
    }

    useEffect(() => {
        if(client){
            dispatch(getSchool(client?.id))
        }
    },[dispatch, t, client])

    useEffect(() => {
        if(school){
            dispatch(getSchoolStaffsBySchool(school._id))
        }
    },[school, dispatch])

    useEffect(() => {
        if(result){
            switch (result) {
                case 'registerSchoolStaffSuccess':
                    Swal.fire({
                        showConfirmButton: true,
                        icon: 'success',
                        text: t('schoolStaff.registerSchoolStaffSuccess')
                    })
                    setSchoolStaffForm(null)
                    break;
                case 'editSchoolStaffSuccess':
                    Swal.fire({
                        showConfirmButton: true,
                        icon: 'success',
                        text: t('schoolStaff.editSchoolStaffSuccess')
                    })
                    setSchoolStaffForm(null)
                    break;
                case 'schoolStaffDeleteSuccess':
                    Swal.fire({
                        showConfirmButton: true,
                        icon: 'success',
                        text: t('schoolStaff.schoolStaffDeleteSuccess')
                    })
                    break;
                case 'schoolStaffEmailError':
                    Swal.fire({
                        showConfirmButton: true,
                        icon: 'warning',
                        text: t('schoolStaff.schoolStaffEmailError')
                    })
                    break;
                default:
                    break;
            }
            dispatch({type: 'SCHOOL_STAFF_RESET_RESULT'})
        }
    // eslint-disable-next-line
    }, [result])

    return (
        <>
            <Container className='cuerpo'>
                <Row className="mb-5">
                    <Col className='d-flex align-items-center'>
                        <img src="/images/menus/experiencia.svg" alt="Base de datos" />
                        <h1 className='ms-2 m-0'>{t('schoolStaff.title')}</h1>
                    </Col>
                </Row>
                <Row className="mt-2">
                    <Col>
                        <Card className='mb-5 sombra lateral'>
                            <Card.Body className='p-5'>
                                <Row>
                                    <Col xs={12} className='d-flex justify-content-end'>
                                        <Button variant='primary' type='button' onClick={() => setSchoolStaffForm({})}>
                                            <i className='fas fa-plus me-2'></i> {t('schoolStaff.createStaff')}
                                        </Button>
                                    </Col>
                                </Row>
                                <Row className='my-3'>
                                    <Col className='my-3'>
                                        {
                                            loading
                                            ?
                                                <div className='text-center'>
                                                    <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                                                </div>
                                            :
                                                <CustomDataTable
                                                    columns={schoolStaffsColumns}
                                                    data={schoolStaffs}
                                                    defaultSortField="name"
                                                    defaultSortAsc={true}
                                                />
                                        }
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            </Container>
            <Modal show={schoolStaffForm ? true : false} size='xl' aria-labelledby="example-custom-modal-styling-title" centered onHide={() => setSchoolStaffForm(null)}>
                <Modal.Header closeButton>
                    <Modal.Title>
                        { schoolStaffForm?.staffId ? t('schoolStaff.editStaff') : t('schoolStaff.createStaff')}
					</Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleSubmit}>
                    <Modal.Body>
                        <Row>
                            <Col xs={12} md={6} className='mb-3'>
                                <Form.Group>
                                    <Form.Label>{t('schoolStaff.name')}</Form.Label>
                                    <Form.Control value={schoolStaffForm?.name || ''} onChange={(e) => setSchoolStaffForm({...schoolStaffForm, name: e.target.value})} required/>
                                </Form.Group>
                            </Col>
                            <Col xs={12} md={6} className='mb-3'>
                                <Form.Group>
                                    <Form.Label>{t('schoolStaff.charge')}</Form.Label>
                                    <Form.Control value={schoolStaffForm?.charge || ''} onChange={(e) => setSchoolStaffForm({...schoolStaffForm, charge: e.target.value})} required/>
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            {/* <Col xs={12} md={6} className='mb-3'> */}
                            <Col xs={12} className='mb-3'>
                                <Form.Group>
                                    <Form.Label>{t('schoolStaff.email')}</Form.Label>
                                    <Form.Control value={schoolStaffForm?.email || ''} onChange={(e) => setSchoolStaffForm({...schoolStaffForm, email: e.target.value})} required/>
                                </Form.Group>
                            </Col>
                            {/* <Col xs={12} md={6} className='mb-3'>
                                <Form.Group>
                                    <Form.Label>{t('schoolStaff.password')}</Form.Label>
                                    <Form.Control value={schoolStaffForm?.password || ''} onChange={(e) => setSchoolStaffForm({...schoolStaffForm, password: e.target.value})} required={!schoolStaffForm?.staffId}/>
                                </Form.Group>
                            </Col> */}
                        </Row>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button type='button' variant='secondary' onClick={() => setSchoolStaffForm(null)}>{t('global.cancel')}</Button>
                        <Button type='submit' variant='primary' >{ schoolStaffForm?.staffId ? t('schoolStaff.editStaff') : t('schoolStaff.createStaff')}</Button>
                    </Modal.Footer>
                </Form>
            </Modal>
        </>
    )
}